import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { DocumentCheckIcon } from '@heroicons/react/24/outline';

const ExcelProcessorNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const operation = data.config?.operation || 'read';
  const sheet = data.config?.sheet || 'Sheet1';

  const getOperationIcon = (op: string) => {
    const icons: Record<string, string> = {
      read: '📖',
      write: '✍️',
      append: '➕',
      update: '🔄'
    };
    return icons[op] || '📊';
  };

  const getOperationLabel = (op: string) => {
    const labels: Record<string, string> = {
      read: 'Olvasás',
      write: 'Írás',
      append: 'Hozzáfűzés',
      update: 'Frissítés'
    };
    return labels[op] || op;
  };

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-green-500 to-green-600 border-2 transition-all duration-200 ${
      selected ? 'border-green-700 shadow-xl scale-105' : 'border-green-400'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <DocumentCheckIcon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-green-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Operation display */}
          <div className="flex items-center mt-1 space-x-2">
            <span className="text-sm">
              {getOperationIcon(operation)}
            </span>
            <span className="text-xs text-green-100">
              {getOperationLabel(operation)}
            </span>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Sheet preview */}
      {sheet && (
        <div className="mt-2 text-xs text-green-100 bg-black bg-opacity-20 px-2 py-1 rounded truncate">
          <strong>Munkalap:</strong> {sheet}
        </div>
      )}

      {/* Execution result */}
      {data.executionData?.data && Array.isArray(data.executionData.data) && (
        <div className="mt-2 text-xs text-green-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <strong>Sorok:</strong> {data.executionData.data.length} db
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-green-700 border-2 border-white"
      />

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-green-700 border-2 border-white"
      />
    </div>
  );
};

export default ExcelProcessorNode;
